import { ColumnDef, Table } from "@tanstack/react-table";
import { Download } from "lucide-react";
import { useMemo } from "react";

import { ContentContainer, PageLayout, PageTitle } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { useGetPriceRemarkProductivityQuery } from "@/services/price-remark/monitoring";
import { ProductivityRow } from "@/services/price-remark/types";

import { StatusByDayChart, StatusByDayDatum } from "../../components/charts";
import { PriceRemarkFilter } from "../../components/filter";
import { KpiCards } from "../../components/kpi-cards";
import { QueryErrorAlert } from "../../components/query-error-alert";
import { ColumnHeader } from "../../components/table/column-header";
import { MonitoringTable } from "../../components/table/monitoring-table";
import { usePriceRemarkFilterState } from "../../ducks/filter/hooks/filterState";
import { exportRowsToXlsx } from "../../utils/export-xlsx";
import { formatLojaLabel } from "../../utils/format-loja";
import { extractQueryErrorMessage } from "../../utils/query-error";

const FIXED_OPERATOR = "OPERADOR_REMARCADOS";

function ritmoPorHora(row: ProductivityRow): string {
  if (!row.minutos || row.minutos <= 0) return "—";
  return `${Math.round((row.etiquetas_validas / row.minutos) * 60)}/h`;
}

function sumFooter(id: keyof ProductivityRow) {
  return ({ table }: { table: Table<ProductivityRow> }) =>
    table
      .getFilteredRowModel()
      .rows.reduce((acc, row) => acc + Number(row.getValue(id as string) || 0), 0);
}

const columns: ColumnDef<ProductivityRow>[] = [
  {
    accessorKey: "dia",
    header: ({ column }) => <ColumnHeader column={column} title="Dia" />,
    meta: { label: "Dia" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap">{row.original.dia}</span>
    ),
  },
  {
    accessorKey: "loja",
    header: ({ column }) => <ColumnHeader column={column} title="Loja" />,
    meta: { label: "Loja" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap">
        {formatLojaLabel(row.original)}
      </span>
    ),
  },
  {
    accessorKey: "operador",
    header: ({ column }) => <ColumnHeader column={column} title="Operador" />,
    meta: { label: "Operador" },
  },
  {
    accessorKey: "etiquetas_validas",
    header: ({ column }) => <ColumnHeader column={column} title="Válidas" />,
    meta: { label: "Válidas" },
    footer: sumFooter("etiquetas_validas"),
  },
  {
    accessorKey: "invalidadas",
    header: ({ column }) => <ColumnHeader column={column} title="Invalidadas" />,
    meta: { label: "Invalidadas" },
    footer: sumFooter("invalidadas"),
  },
  {
    accessorKey: "falha_impressao",
    header: ({ column }) => <ColumnHeader column={column} title="Falhas" />,
    meta: { label: "Falhas" },
    footer: sumFooter("falha_impressao"),
  },
  {
    accessorKey: "itens_distintos",
    header: ({ column }) => <ColumnHeader column={column} title="Itens" />,
    meta: { label: "Itens" },
    footer: sumFooter("itens_distintos"),
  },
  {
    accessorKey: "reimpressoes",
    header: ({ column }) => <ColumnHeader column={column} title="Reimpressões" />,
    meta: { label: "Reimpressões" },
    footer: sumFooter("reimpressoes"),
  },
  { accessorKey: "inicio", header: "Início", meta: { label: "Início" } },
  { accessorKey: "fim", header: "Fim", meta: { label: "Fim" } },
  {
    id: "ritmo",
    header: "Ritmo",
    enableSorting: false,
    meta: { label: "Ritmo" },
    cell: ({ row }) => ritmoPorHora(row.original),
  },
];

export function PriceRemarkProductivity() {
  const { filters } = usePriceRemarkFilterState();
  const {
    data = [],
    isFetching,
    isError,
    error,
    refetch,
  } = useGetPriceRemarkProductivityQuery({
    dtIni: filters.dtIni,
    dtFim: filters.dtFim,
    loja: filters.loja,
    tipo: filters.tipo,
  });

  const kpis = useMemo(() => {
    const sum = (key: keyof ProductivityRow) =>
      data.reduce((acc, row) => acc + Number(row[key] || 0), 0);
    const validas = sum("etiquetas_validas");
    const invalidadas = sum("invalidadas");
    const operadores = new Set(data.map((row) => row.operador)).size;
    const total = validas + invalidadas + sum("falha_impressao");
    const taxa = total > 0 ? Math.round((invalidadas / total) * 100) : 0;
    return [
      { label: "Válidas", value: validas, tone: "positive" as const },
      { label: "Invalidadas", value: invalidadas, tone: "warning" as const },
      { label: "Falhas", value: sum("falha_impressao"), tone: "danger" as const },
      { label: "Reimpressões", value: sum("reimpressoes") },
      { label: "Operadores", value: operadores },
      { label: "Taxa invalidação", value: `${taxa}%` },
    ];
  }, [data]);

  const chartData = useMemo<StatusByDayDatum[]>(() => {
    const byDay = new Map<string, StatusByDayDatum>();
    data.forEach((row) => {
      const current =
        byDay.get(row.dia) ??
        ({ dia: row.dia, validas: 0, invalidadas: 0, falhas: 0 } as StatusByDayDatum);
      current.validas += Number(row.etiquetas_validas || 0);
      current.invalidadas += Number(row.invalidadas || 0);
      current.falhas += Number(row.falha_impressao || 0);
      byDay.set(row.dia, current);
    });
    return [...byDay.values()].sort((a, b) => a.dia.localeCompare(b.dia));
  }, [data]);

  const totalRows = data.length;
  const fixedRows = data.filter((row) => row.operador === FIXED_OPERATOR).length;
  const showFixedAlert = totalRows > 0 && fixedRows / totalRows > 0.5;

  return (
    <PageLayout>
      <PageTitle
        title="Produtividade por operador"
        description="Etiquetas válidas, reimpressões e ritmo por operador/loja/dia."
      />
      <ContentContainer>
        <div className="space-y-4">
          <PriceRemarkFilter showTipo />

          {isError ? (
            <QueryErrorAlert
              message={extractQueryErrorMessage(error)}
              onRetry={refetch}
            />
          ) : (
            <>
              {showFixedAlert && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  A maioria das linhas está no operador fixo{" "}
                  <b>OPERADOR_REMARCADOS</b> — o login do coletor está desligado
                  e a visão por operador fica cega. Habilite o login via banco
                  (TI/DBA).
                </div>
              )}

              <KpiCards items={kpis} />

              {chartData.length > 0 && <StatusByDayChart data={chartData} />}

              <MonitoringTable
                columns={columns}
                data={data}
                isLoading={isFetching}
                searchPlaceholder="Buscar operador/loja..."
                emptyMessage="Sem dados no período."
                toolbarRight={
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    disabled={data.length === 0}
                    onClick={() =>
                      exportRowsToXlsx(
                        data as unknown as Record<string, unknown>[],
                        "produtividade-etiqueta-remarcados",
                      )
                    }
                  >
                    <Download className="mr-1 h-4 w-4" /> Exportar xlsx
                  </Button>
                }
              />
            </>
          )}
        </div>
      </ContentContainer>
    </PageLayout>
  );
}

export default PriceRemarkProductivity;
